import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, Cell,
  PieChart, Pie,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  ArrowLeft, Users, BarChart3, PieChart as PieIcon, Clock, CheckCircle2,
  Loader2, Filter, X, CalendarIcon, TrendingUp, Search, UserRound, Info
} from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: '#F59E0B',
  in_progress: '#3B82F6',
  hold: '#F97316',
  completed: '#10B981',
  defunct: '#EF4444',
};

const STATUS_META = {
  pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  in_progress: { label: 'In Progress', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  hold: { label: 'Hold', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  defunct: { label: 'Defunct', color: 'bg-red-50 text-red-700 border-red-200' },
};

const DATE_PRESETS = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

// Convert hours → formatted "X.X days" (or "< 1 day" if under 24h)
const formatTurnaroundDays = (hours) => {
  if (hours == null) return '—';
  const days = hours / 24;
  if (days < 1) return `< 1 day`;
  return `${days.toFixed(1)} days`;
};

const TURNAROUND_TOOLTIP = 'Average time from when the task was first moved to In Progress until it was marked Completed. Reflects active working time only — excludes time spent in Pending or Hold queues. Shown in days (24h = 1 day).';

export default function AssigneeTrackerPage() {
  const { assigneeId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const currentEmail = (currentUser?.email || '').toLowerCase();

  // "first.last@wellversed.in" → "First Last" for a friendly Submitted By cell.
  const nameFromEmail = (email) => {
    if (!email) return '—';
    const local = String(email).split('@')[0] || email;
    return local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ') || email;
  };

  const [assignee, setAssignee] = useState(null);
  const [perf, setPerf] = useState({ rows: [], totals: { total: 0, pending: 0, in_progress: 0, hold: 0, completed: 0, completion_rate: 0, avg_turnaround_hours: null } });
  const [tasks, setTasks] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [brandFilter, setBrandFilter] = useState('all');
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [taskSearch, setTaskSearch] = useState('');
  // Submitter dropdown — logged-in user pinned at top with "me" tag.
  const [submitterFilter, setSubmitterFilter] = useState('all');

  const dateRange = useMemo(() => {
    const now = new Date();
    if (datePreset === 'today') return { from: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString(), to: null };
    if (datePreset === '7d') return { from: subDays(now, 7).toISOString(), to: null };
    if (datePreset === '30d') return { from: subDays(now, 30).toISOString(), to: null };
    if (datePreset === '90d') return { from: subDays(now, 90).toISOString(), to: null };
    if (datePreset === 'custom') return { from: customFrom ? customFrom.toISOString() : null, to: customTo ? customTo.toISOString() : null };
    return { from: null, to: null };
  }, [datePreset, customFrom, customTo]);

  // Fetch assignee master list + brands + task types (once)
  useEffect(() => {
    (async () => {
      try {
        const [aRes, bRes, tRes] = await Promise.all([
          fetch(`${API_URL}/api/masters/assignees`, { credentials: 'include' }),
          fetch(`${API_URL}/api/brands`, { credentials: 'include' }),
          fetch(`${API_URL}/api/masters/task-types`, { credentials: 'include' }),
        ]);
        if (aRes.ok) {
          const list = await aRes.json();
          const match = list.find(a => a.id === assigneeId);
          setAssignee(match || null);
        }
        if (bRes.ok) setBrands(await bRes.json());
        if (tRes.ok) setTaskTypes(await tRes.json());
      } catch (e) {}
    })();
  }, [assigneeId]);

  // Fetch performance + tasks when filters change
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('assignee_id', assigneeId);
        if (brandFilter !== 'all') params.append('brand_name', brandFilter);
        if (dateRange.from) params.append('date_from', dateRange.from);
        if (dateRange.to) params.append('date_to', dateRange.to);
        if (taskTypeFilter !== 'all') params.append('task_type', taskTypeFilter);
        if (mediaTypeFilter !== 'all') params.append('media_type', mediaTypeFilter);
        if (statusFilter !== 'all') params.append('status', statusFilter);

        const taskParams = new URLSearchParams();
        taskParams.append('assignee_id', assigneeId);
        if (brandFilter !== 'all') taskParams.append('brand_name', brandFilter);
        if (statusFilter !== 'all') taskParams.append('status', statusFilter);
        if (taskTypeFilter !== 'all') taskParams.append('task_type', taskTypeFilter);

        const [pRes, tRes] = await Promise.all([
          fetch(`${API_URL}/api/workspace/assignee-performance?${params.toString()}`, { credentials: 'include' }),
          fetch(`${API_URL}/api/tasks?${taskParams.toString()}`, { credentials: 'include' }),
        ]);
        if (pRes.ok) setPerf(await pRes.json());
        if (tRes.ok) setTasks(await tRes.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [assigneeId, brandFilter, taskTypeFilter, datePreset, customFrom, customTo, mediaTypeFilter, statusFilter, dateRange.from, dateRange.to]);

  const clearFilters = () => {
    setDatePreset('all'); setCustomFrom(null); setCustomTo(null);
    setBrandFilter('all'); setTaskTypeFilter('all'); setMediaTypeFilter('all'); setStatusFilter('all'); setTaskSearch('');
    setSubmitterFilter('all');
  };
  const hasActive = datePreset !== 'all' || brandFilter !== 'all' || taskTypeFilter !== 'all' || mediaTypeFilter !== 'all' || statusFilter !== 'all' || submitterFilter !== 'all' || !!taskSearch;

  // Unique submitters for the dropdown, logged-in user pinned at top.
  const submitters = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const em = (t.submitter_email || '').trim();
      if (!em) continue;
      const key = em.toLowerCase();
      if (!map.has(key)) map.set(key, em);
    }
    const currentEm = (currentUser?.email || '').trim();
    if (currentEm && !map.has(currentEm.toLowerCase())) map.set(currentEm.toLowerCase(), currentEm);
    const list = Array.from(map.values()).sort((a, b) => a.localeCompare(b));
    if (currentEm) {
      const idx = list.findIndex(x => x.toLowerCase() === currentEm.toLowerCase());
      if (idx > 0) {
        const [me] = list.splice(idx, 1);
        list.unshift(me);
      }
    }
    return list;
  }, [tasks, currentUser]);

  // Apply client-side media_type + search on task list (the backend already filters others via perf aggregation; tasks list is direct)
  const filteredTasks = useMemo(() => {
    let r = [...tasks];
    const CONTENT_KEYWORDS = /(blog|script|caption|article|post|copy|email|newsletter|social)/i;
    if (mediaTypeFilter !== 'all') {
      r = r.filter(t => {
        const tt = taskTypes.find(x => x.name === t.task_type);
        if (mediaTypeFilter === 'content') {
          if (tt?.kind === 'content') return true;
          if (!tt && CONTENT_KEYWORDS.test(t.task_type || '')) return true;
          return false;
        }
        return tt?.media_type === mediaTypeFilter;
      });
    }
    if (submitterFilter && submitterFilter !== 'all') {
      const wanted = submitterFilter.toLowerCase();
      r = r.filter(t => (t.submitter_email || '').toLowerCase() === wanted);
    }
    if (dateRange.from) r = r.filter(t => t.created_at >= dateRange.from);
    if (dateRange.to) r = r.filter(t => t.created_at <= dateRange.to);
    if (taskSearch) {
      const s = taskSearch.toLowerCase();
      r = r.filter(t =>
        (t.task_id || '').toLowerCase().includes(s) ||
        (t.design_brief || '').toLowerCase().includes(s) ||
        (t.task_type || '').toLowerCase().includes(s) ||
        (t.brand_name || '').toLowerCase().includes(s) ||
        (t.submitter_email || '').toLowerCase().includes(s)
      );
    }
    return r;
  }, [tasks, taskTypes, mediaTypeFilter, submitterFilter, dateRange.from, dateRange.to, taskSearch]);

  // Breakdown by brand (for this assignee)
  const brandBreakdown = useMemo(() => {
    const map = {};
    for (const t of filteredTasks) {
      const key = t.brand_name || 'Unassigned Brand';
      const g = map[key] || (map[key] = { brand: key, total: 0, pending: 0, in_progress: 0, hold: 0, completed: 0 });
      g.total += 1;
      g[t.status] = (g[t.status] || 0) + 1;
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredTasks]);

  const barData = useMemo(() => brandBreakdown.slice(0, 8).map(b => ({
    name: b.brand.length > 12 ? b.brand.slice(0, 11) + '…' : b.brand,
    Pending: b.pending || 0,
    'In Progress': b.in_progress || 0,
    Hold: b.hold || 0,
    Completed: b.completed || 0,
  })), [brandBreakdown]);

  const donutData = useMemo(() => [
    { name: 'Pending', value: perf.totals.pending, color: STATUS_COLORS.pending },
    { name: 'In Progress', value: perf.totals.in_progress, color: STATUS_COLORS.in_progress },
    { name: 'Hold', value: perf.totals.hold || 0, color: STATUS_COLORS.hold },
    { name: 'Completed', value: perf.totals.completed, color: STATUS_COLORS.completed },
    { name: 'Defunct', value: perf.totals.defunct || 0, color: STATUS_COLORS.defunct },
  ].filter(d => d.value > 0), [perf.totals]);

  const assigneeName = assignee?.name || 'Assignee';
  const initials = (assigneeName.match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assignee-tracker-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2EB4AC] to-[#25908A] text-white flex items-center justify-center font-bold text-lg shadow-md shadow-[#2EB4AC]/25">
              {initials || <UserRound className="h-6 w-6" />}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{assigneeName}</h1>
              <p className="text-sm text-slate-500">{perf.totals.total} tasks · {perf.totals.completion_rate}% completion · {perf.totals.avg_turnaround_hours != null ? `${formatTurnaroundDays(perf.totals.avg_turnaround_hours)} avg turnaround` : 'No completions yet'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Filter className="h-5 w-5 text-slate-500" /><CardTitle className="text-lg">Filters</CardTitle></div>
            {hasActive && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500" data-testid="assignee-clear-filters">
                <X className="h-4 w-4 mr-1" /> Clear all
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="h-10" data-testid="assignee-date-preset"><SelectValue /></SelectTrigger>
              <SelectContent>{DATE_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={brandFilter} onValueChange={setBrandFilter}>
              <SelectTrigger className="h-10" data-testid="assignee-brand-filter"><SelectValue placeholder="Brand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={taskTypeFilter} onValueChange={setTaskTypeFilter}>
              <SelectTrigger className="h-10" data-testid="assignee-task-type-filter"><SelectValue placeholder="Task Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Task Types</SelectItem>
                {taskTypes.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={mediaTypeFilter} onValueChange={setMediaTypeFilter}>
              <SelectTrigger className="h-10" data-testid="assignee-media-filter"><SelectValue placeholder="Media" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Media</SelectItem>
                <SelectItem value="static">Static</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="content">Content</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10" data-testid="assignee-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="hold">Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="defunct">Defunct</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {datePreset === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('justify-start text-left h-10', !customFrom && 'text-slate-400')}>
                    <CalendarIcon className="h-4 w-4 mr-2" />{customFrom ? format(customFrom, 'PP') : 'From date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={customFrom} onSelect={setCustomFrom} /></PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('justify-start text-left h-10', !customTo && 'text-slate-400')}>
                    <CalendarIcon className="h-4 w-4 mr-2" />{customTo ? format(customTo, 'PP') : 'To date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={customTo} onSelect={setCustomTo} /></PopoverContent>
              </Popover>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MiniStat label="Total" value={perf.totals.total} icon={BarChart3} color="teal" />
        <MiniStat label="Pending" value={perf.totals.pending} icon={Clock} color="amber" />
        <MiniStat label="In Progress" value={perf.totals.in_progress} icon={Loader2} color="blue" />
        <MiniStat label="Completed" value={perf.totals.completed} icon={CheckCircle2} color="emerald" />
        <MiniStat
          label="Avg Turnaround"
          value={formatTurnaroundDays(perf.totals.avg_turnaround_hours)}
          icon={TrendingUp}
          color="slate"
          tooltip={TURNAROUND_TOOLTIP}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#2EB4AC]" /> Tasks per Brand</CardTitle></CardHeader>
          <CardContent>
            {barData.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-10">No data to display</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={barData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RechartsTooltip contentStyle={{ fontSize: '12px', borderRadius: '8px' }} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="Pending" stackId="a" fill={STATUS_COLORS.pending} />
                  <Bar dataKey="In Progress" stackId="a" fill={STATUS_COLORS.in_progress} />
                  <Bar dataKey="Hold" stackId="a" fill={STATUS_COLORS.hold} />
                  <Bar dataKey="Completed" stackId="a" fill={STATUS_COLORS.completed} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><PieIcon className="h-4 w-4 text-[#2EB4AC]" /> Status Distribution</CardTitle></CardHeader>
          <CardContent>
            {donutData.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-10">No data to display</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" label={(entry) => `${entry.name}: ${entry.value}`}>
                    {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <RechartsTooltip contentStyle={{ fontSize: '12px', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent tasks table */}
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-[#2EB4AC]" /> Tasks for {assigneeName} ({filteredTasks.length})</CardTitle>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Select value={submitterFilter} onValueChange={setSubmitterFilter}>
                <SelectTrigger className="h-9 w-[220px] text-xs border-slate-200 bg-white" data-testid="submitter-filter">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <UserRound className="h-3 w-3 text-slate-400 shrink-0" />
                    <SelectValue placeholder="All submitters" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All submitters</SelectItem>
                  {submitters.map((em) => {
                    const isMe = em.toLowerCase() === currentEmail;
                    return (
                      <SelectItem key={em} value={em}>
                        <div className="flex items-center gap-2">
                          <span>{nameFromEmail(em)}</span>
                          {isMe && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#2EB4AC]/10 text-[#2EB4AC] border border-[#2EB4AC]/30">
                              me
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search task ID, brief, submitter..." value={taskSearch} onChange={(e) => setTaskSearch(e.target.value)} className="pl-10 h-9" data-testid="assignee-task-search" />
              </div>
            </div>
          </div>
          {hasActive && (
            <div className="flex items-center gap-2 flex-wrap mt-3" data-testid="assignee-active-filter-chips">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Active:</span>
              {datePreset !== 'all' && (
                <FilterChip label={`Date: ${DATE_PRESETS.find(p => p.value === datePreset)?.label || datePreset}`} onRemove={() => { setDatePreset('all'); setCustomFrom(null); setCustomTo(null); }} />
              )}
              {brandFilter !== 'all' && <FilterChip label={`Brand: ${brandFilter}`} onRemove={() => setBrandFilter('all')} />}
              {taskTypeFilter !== 'all' && <FilterChip label={`Type: ${taskTypeFilter}`} onRemove={() => setTaskTypeFilter('all')} />}
              {mediaTypeFilter !== 'all' && <FilterChip label={`Media: ${mediaTypeFilter.charAt(0).toUpperCase() + mediaTypeFilter.slice(1)}`} onRemove={() => setMediaTypeFilter('all')} />}
              {statusFilter !== 'all' && <FilterChip label={`Status: ${STATUS_META[statusFilter]?.label || statusFilter}`} onRemove={() => setStatusFilter('all')} />}
              {submitterFilter !== 'all' && <FilterChip label={`Submitter: ${nameFromEmail(submitterFilter)}${submitterFilter.toLowerCase() === currentEmail ? ' (me)' : ''}`} onRemove={() => setSubmitterFilter('all')} />}
              {taskSearch && <FilterChip label={`Search: "${taskSearch}"`} onRemove={() => setTaskSearch('')} />}
              <button onClick={clearFilters} className="text-[11px] text-slate-500 hover:text-[#2EB4AC] underline ml-1" data-testid="assignee-table-clear">Clear all</button>
            </div>
          )}
        </CardHeader>
        {loading ? (
          <div className="text-center py-10 text-sm text-slate-500">Loading...</div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">No tasks for these filters</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="whitespace-nowrap">Task ID</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Task Type</TableHead>
                  <TableHead>Brief</TableHead>
                  <TableHead className="whitespace-nowrap">Submitted By</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Submitted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTasks.slice(0, 100).map(t => (
                  <TableRow
                    key={t.id}
                    data-testid={`assignee-task-${t.id}`}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/tasks/brand/${encodeURIComponent(t.brand_name)}?task=${encodeURIComponent(t.task_id || t.id)}`)}
                    title="Open task details"
                  >
                    <TableCell className="font-medium text-[#2EB4AC]">{t.task_id}</TableCell>
                    <TableCell className="text-slate-700">{t.brand_name}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{t.task_type}</TableCell>
                    <TableCell className="text-slate-500 text-xs max-w-[240px] truncate">{t.design_brief}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm" data-testid={`submitted-by-cell-${t.task_id}`}>
                      <div className="flex flex-col leading-tight">
                        <span className="text-slate-900">{nameFromEmail(t.submitter_email)}</span>
                        <span className="text-[10px] text-slate-400 truncate max-w-[140px]" title={t.submitter_email}>{t.submitter_email}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={STATUS_META[t.status]?.color || 'bg-slate-50 text-slate-700 border-slate-200'}>
                        {STATUS_META[t.status]?.label || t.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-xs text-slate-500">{t.created_at ? format(new Date(t.created_at), 'dd MMM') : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredTasks.length > 100 && (
              <p className="text-center py-2 text-[11px] text-slate-400">Showing first 100 of {filteredTasks.length} tasks</p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#2EB4AC]/10 text-[#2EB4AC] text-[11px] font-medium border border-[#2EB4AC]/20">
    {label}
    <button onClick={onRemove} className="hover:bg-[#2EB4AC]/20 rounded-full p-0.5 transition-colors" aria-label={`Remove ${label}`}>
      <X className="h-2.5 w-2.5" />
    </button>
  </span>
);

const MiniStat = ({ label, value, icon: Icon, color, tooltip }) => {
  const map = {
    slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
    teal: { bg: 'bg-[#2EB4AC]/10', text: 'text-[#2EB4AC]' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  }[color] || { bg: 'bg-slate-100', text: 'text-slate-600' };
  return (
    <Card className="border border-slate-200 bg-white">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ${map.bg} flex items-center justify-center`}><Icon className={`h-4 w-4 ${map.text}`} /></div>
        <div className="min-w-0 flex-1">
          <p className="text-xl font-bold text-slate-900 leading-none truncate">{value}</p>
          <div className="flex items-center gap-1 mt-1">
            <p className="text-[11px] text-slate-500">{label}</p>
            {tooltip && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-slate-400 hover:text-[#2EB4AC] transition-colors" aria-label={`About ${label}`}>
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-[11px] leading-relaxed">
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
